import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  IconButton,
  Divider,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Chip,
  Fab,
  Tooltip,
  Drawer,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Star,
  StarBorder,
  Comment as CommentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { dashboardsService, favoritosService, comentariosService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/ContextProvider';
import { ChatIAButton } from '../components/ChatIA';
import HtmlEmbed from '../components/HtmlEmbed';
import CuboInterativo from '../components/bi/CuboInterativo';

const DashboardPage: React.FC = () => {
  const { submenuId } = useParams<{ submenuId: string }>();
  const navigate = useNavigate();
  const { user, municipioAtivo } = useAuth();
  const { refetch, menus } = useAppContext();
  const [dashboard, setDashboard] = useState<any>(null);
  const [comentarios, setComentarios] = useState<any[]>([]);
  const [novoComentario, setNovoComentario] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingComentarios, setLoadingComentarios] = useState(false);
  const [comentariosOpen, setComentariosOpen] = useState(false);
  const [biTab, setBiTab] = useState(0);
  const dashboardAreaRef = React.useRef<HTMLDivElement>(null);

  const loadDashboard = useCallback(async () => {
    if (!submenuId) return;
    
    setLoading(true);
    try {
      const data = await dashboardsService.getDashboard(Number(submenuId));
      setDashboard(data);
      
      // Debug: verificar se o código HTML está presente
      if (data.tipo_conteudo === 'html' && data.codigo_html) {
        console.log('✅ Código HTML encontrado:', data.codigo_html.substring(0, 100) + '...');
      } else if (data.tipo_conteudo === 'html') {
        console.warn('⚠️ Tipo HTML mas sem código HTML!');
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [submenuId]);

  useEffect(() => {
    if (submenuId) {
      loadDashboard();
      loadComentarios();
    }
  }, [submenuId, municipioAtivo?.id, loadDashboard]);

  // Verificar se o dashboard ainda existe quando o município mudar
  useEffect(() => {
    if (municipioAtivo && submenuId && menus.length > 0) {
      const submenuExists = menus.some((menu) =>
        menu.submenus?.some((submenu: any) => submenu.id === Number(submenuId))
      );
      if (!submenuExists) {
        // Dashboard não existe no novo município, redirecionar para home
        navigate('/');
      }
    }
  }, [municipioAtivo?.id, menus, submenuId, navigate]);

  const loadComentarios = async () => {
    try {
      const data = await comentariosService.getComentarios(Number(submenuId));
      setComentarios(data);
    } catch (error) {
      console.error('Erro ao carregar comentários:', error);
    }
  };

  const handleToggleFavorito = async () => {
    try {
      if (dashboard.is_favorito) {
        await favoritosService.removeFavorito(Number(submenuId));
      } else {
        await favoritosService.addFavorito(Number(submenuId));
      }
      await loadDashboard();
      await refetch();
    } catch (error) {
      console.error('Erro ao alterar favorito:', error);
    }
  };

  const handleAddComentario = async () => {
    if (!novoComentario.trim()) return;

    setLoadingComentarios(true);
    try {
      await comentariosService.createComentario(Number(submenuId), novoComentario);
      setNovoComentario('');
      await loadComentarios();
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
    } finally {
      setLoadingComentarios(false);
    }
  };

  // Build context for AI chat from dashboard content
  const dashboardContexto = useMemo(() => {
    if (!dashboard) return undefined;
    let ctx = `== DASHBOARD ==\nNome: ${dashboard.nome || 'N/A'}\n`;
    if (dashboard.tipo_conteudo === 'html' && dashboard.codigo_html) {
      // Extract text content from HTML (strip tags)
      const text = dashboard.codigo_html
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (text.length > 0) {
        ctx += `\n== CONTEUDO DO DASHBOARD ==\n${text.slice(0, 4000)}\n`;
      }
    } else if (dashboard.dashboard_url) {
      ctx += `Tipo: Dashboard externo (iframe)\nURL: ${dashboard.dashboard_url}\n`;
      ctx += `\n[O conteudo sera buscado automaticamente pelo servidor.]\n`;
    }
    return ctx;
  }, [dashboard]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Container>
        <Typography variant="h6">Dashboard não encontrado</Typography>
      </Container>
    );
  }

  // Embed do Superset (BI): habilita as abas "Painel" + "Cubo interativo (drag & drop)".
  const isSupersetBI =
    dashboard.tipo_conteudo === 'html' &&
    !!dashboard.codigo_html &&
    (dashboard.codigo_html.includes('superset-bi') || dashboard.codigo_html.includes('embedDashboard'));

  // Dashboards de BI que exibem a aba "Cubo interativo" (drag & drop):
  // Visao Geral + os paineis cujos datasets sao fontes do cubo
  // (atendimentos / vacinacao / dispensacao / satisfacao em CUBO_FONTES — backend/src/bi/bi.service.ts).
  //
  // O gate e pelo SLUG do painel: a migration 035_bi_embed_por_slug trocou o UUID fixo do
  // codigo_html por resolucao em runtime (var SLUG + GET /bi/embed?slug=), entao nao existe
  // mais UUID no codigo_html para comparar — e o UUID ainda varia por municipio
  // ("<slug>-<ibge>"). A lista de UUIDs permanece como fallback para submenus antigos,
  // anteriores a 035, que ainda tragam o UUID fixo.
  const CUBO_DASHBOARD_SLUGS = [
    'saude-visao-geral', // Saude — Visao Geral (BI)
    'pronto-atendimento-atendimentos', // fonte: atendimentos
    'vacinacao-campanhas', // fonte: vacinacao
    'dispensacao-medicamentos', // fonte: dispensacao
    'pesquisa-satisfacao', // fonte: satisfacao
  ];
  const CUBO_DASHBOARD_UUIDS = [
    '515614c9-8b0d-4ae4-be96-d69afe322af2', // Saude — Visao Geral (BI)
    '282d49ed-395c-4fb4-ae84-7bb60fa8f2a8', // Pronto Atendimento — Atendimentos
    '8c9cab7d-13b3-4715-8ec9-a246a8eb1e76', // Vacinacao — Campanhas e Espontaneas
    '3d394e95-8c70-4aaa-abd7-b18af84d534a', // Dispensacao de Medicamentos (Farmacia)
    '83968dc9-a4ae-4bcb-9cc6-bcef8ea9f554', // Pesquisa de Satisfacao
  ];
  const slugDoPainel = dashboard.codigo_html?.match(/var SLUG = "([^"]+)"/)?.[1] ?? '';
  const showCubo =
    isSupersetBI &&
    (CUBO_DASHBOARD_SLUGS.includes(slugDoPainel) ||
      CUBO_DASHBOARD_UUIDS.some((uuid) => dashboard.codigo_html!.includes(uuid)));

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Botões flutuantes: superior direito, um abaixo do outro.
          top mais baixo p/ não sobrepor o badge de auto-refresh ("Atualiza em Ns")
          dos painéis de BI — nos painéis com a barra de abas do cubo o badge desce ~41px
          e chegava a ~138px (sob o Fab de favoritos em top:128). */}
      <Tooltip title={dashboard.is_favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}>
        <Fab
          color="primary"
          size="small"
          onClick={handleToggleFavorito}
          sx={{
            position: 'fixed',
            top: 176,
            right: 24,
            zIndex: 1000,
            boxShadow: 3,
          }}
        >
          {dashboard.is_favorito ? <Star /> : <StarBorder />}
        </Fab>
      </Tooltip>

      <Tooltip title="Comentários">
        <Fab
          color="primary"
          size="small"
          onClick={() => setComentariosOpen(true)}
          sx={{
            position: 'fixed',
            top: 232,
            right: 24,
            zIndex: 1000,
            boxShadow: 3,
          }}
        >
          <CommentIcon />
        </Fab>
      </Tooltip>

      {/* Dashboard em tela cheia */}
      <Box
        ref={dashboardAreaRef}
        sx={{
          width: '100%',
          height: 'calc(100vh - 64px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#f5f5f5',
        }}
      >
        {showCubo ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Tabs
              value={biTab}
              onChange={(_, v) => setBiTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', minHeight: 40 }}
            >
              <Tab label="Painel" sx={{ minHeight: 40 }} />
              <Tab label="Cubo interativo (drag & drop)" sx={{ minHeight: 40 }} />
            </Tabs>
            <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {/* Mantem o embed montado (display none) p/ nao re-inicializar o Superset ao trocar de aba */}
              <Box sx={{ display: biTab === 0 ? 'block' : 'none', height: '100%' }}>
                {/* key por municipio: trocar de municipio re-monta o embed p/ cunhar guest token com o RLS novo */}
                <HtmlEmbed key={`bi-${municipioAtivo?.id ?? 'none'}`} html={dashboard.codigo_html} minHeight="100%" />
              </Box>
              {biTab === 1 && <CuboInterativo />}
            </Box>
          </Box>
        ) : dashboard.tipo_conteudo === 'html' && dashboard.codigo_html ? (
          <HtmlEmbed key={`html-${municipioAtivo?.id ?? 'none'}`} html={dashboard.codigo_html} minHeight="100%" />
        ) : dashboard.dashboard_url ? (
          <iframe
            src={dashboard.dashboard_url}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title={dashboard.nome}
          />
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>Conteúdo do dashboard não configurado</Typography>
          </Box>
        )}
      </Box>

      {/* Drawer de Comentários (abre com animação ao clicar no Fab) */}
      <Drawer
        anchor="right"
        open={comentariosOpen}
        onClose={() => setComentariosOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 380 },
            maxWidth: '100%',
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        <AppBar position="static" elevation={0} sx={{ borderRadius: 0 }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CommentIcon />
              <Typography variant="h6">Comentários</Typography>
            </Box>
            <IconButton color="inherit" onClick={() => setComentariosOpen(false)} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))', overflow: 'hidden' }}>
          {/* Formulário para novo comentário */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, flexShrink: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Novo comentário
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder="Adicione um comentário..."
              value={novoComentario}
              onChange={(e) => setNovoComentario(e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleAddComentario}
              disabled={loadingComentarios || !novoComentario.trim()}
            >
              {loadingComentarios ? <CircularProgress size={22} color="inherit" /> : 'Enviar'}
            </Button>
          </Paper>

          <Divider sx={{ my: 1 }} />

          {/* Lista dos últimos comentários */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Últimos comentários
          </Typography>
          <List sx={{ flex: 1, overflow: 'auto', py: 0, minHeight: 0 }}>
            {comentarios.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4, px: 2 }}>
                Nenhum comentário ainda. Seja o primeiro!
              </Typography>
            ) : (
              comentarios.map((comentario) => (
                <ListItem key={comentario.id} alignItems="flex-start" sx={{ px: 0, py: 1.5 }} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" component="span">
                          {comentario.usuario_nome}
                        </Typography>
                        <Chip
                          label={comentario.status}
                          size="small"
                          color={comentario.status === 'aberto' ? 'primary' : 'success'}
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="div">
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          {comentario.texto}
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary">
                          {new Date(comentario.created_at).toLocaleString('pt-BR')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Drawer>

      {/* Botão do Chat IA (terceiro, abaixo de Favorito e Comentários) */}
      <ChatIAButton
        contexto="dashboard"
        dashboardId={dashboard?.id}
        submenuId={parseInt(submenuId || '0')}
        dadosContexto={dashboardContexto}
        captureRef={dashboardAreaRef}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default DashboardPage;
